/**
 * Queue Service - Client for queue management Cloud Functions
 */

import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  Unsubscribe,
  Timestamp
} from 'firebase/firestore';
import { functions, db } from '../lib/firebase';
import type { QueueTicket } from '../types';

// Re-export for convenience
export type { QueueTicket };

export interface TakeTicketRequest {
  branchId: string;
  serviceId?: string;
  barberId?: string;
}

export interface TakeTicketResponse {
  success: boolean;
  queueId: string;
  position: number;
  message: string;
}

export interface CompanionInput {
  name: string;
  type: 'child' | 'elder';
  serviceId: string;
  barberId: string; // 'any' for automatic assignment
}

export interface TakeTicketWithCompanionsRequest {
  branchId: string;
  mainTicket: {
    serviceId: string;
    barberId: string;
  };
  companions: CompanionInput[];
}

export interface TakeTicketWithCompanionsResponse {
  success: boolean;
  queueId: string;
  companionQueueIds: string[];
  position: number;
  message: string;
}

export interface AdvanceQueueRequest {
  branchId: string;
  barberId?: string;
  graceMinutes?: 5 | 10 | 15 | 20; // Optional grace minutes for client to arrive
}

export interface AdvanceQueueResponse {
  success: boolean;
  ticket?: {
    queueId: string;
    ticketNumber: string;
    userId: string;
    position: number;
  };
  message?: string;
}

export interface MarkArrivalRequest {
  queueId: string;
}

export interface MarkArrivalResponse {
  success: boolean;
  message: string;
}

export interface StartServiceRequest {
  queueId: string;
}

export interface StartServiceResponse {
  success: boolean;
  message: string;
}

export interface CompleteTicketRequest {
  queueId: string;
}

export interface CompleteTicketResponse {
  success: boolean;
  message: string;
}

export interface CancelTicketRequest {
  queueId: string;
  reason?: string;
}

export interface CancelTicketResponse {
  success: boolean;
  message: string;
}

export interface RemoveExpiredTicketRequest {
  queueId: string;
}

export interface RemoveExpiredTicketResponse {
  success: boolean;
  message: string;
  queueId: string;
}

export interface ReorderQueueRequest {
  ticketPositions: Array<{ queueId: string; position: number }>;
}

export interface ReorderQueueResponse {
  success: boolean;
  message: string;
}

export interface ClearClientHistoryRequest {}

export interface ClearClientHistoryResponse {
  success: boolean;
  message: string;
  deletedCount: number;
}

interface ArchiveNotificationsResponse {
  success: boolean;
  message: string;
  archivedCount: number;
}

// Overtime ticket request interfaces
export interface RequestOvertimeTicketRequest {
  branchId: string;
  serviceId: string;
  barberId: string;
  barberWorkEnd: string;
  estimatedServiceEnd: string;
  overtimeMinutes: number;
}

export interface RequestOvertimeTicketResponse {
  success: boolean;
  queueId: string;
  message: string;
  expiresAt?: number; // Timestamp in ms when request expires
}

export interface RespondOvertimeRequest {
  queueId: string;
  accept: boolean;
  reason?: string; // OvertimeRejectionReason if rejecting
  notes?: string;
}

export interface RespondOvertimeResponse {
  success: boolean;
  message: string;
}

/**
 * Cloud Functions callable references
 */
const takeTicketFn = httpsCallable<TakeTicketRequest, TakeTicketResponse>(functions, 'takeTicket');
const takeTicketWithCompanionsFn = httpsCallable<TakeTicketWithCompanionsRequest, TakeTicketWithCompanionsResponse>(functions, 'takeTicketWithCompanions');
const advanceQueueFn = httpsCallable<AdvanceQueueRequest, AdvanceQueueResponse>(functions, 'advanceQueue');
const markArrivalFn = httpsCallable<MarkArrivalRequest, MarkArrivalResponse>(functions, 'markArrival');
const startServiceFn = httpsCallable<StartServiceRequest, StartServiceResponse>(functions, 'startService');
const completeTicketFn = httpsCallable<CompleteTicketRequest, CompleteTicketResponse>(functions, 'completeTicket');
const cancelTicketFn = httpsCallable<CancelTicketRequest, CancelTicketResponse>(functions, 'cancelTicket');
const removeExpiredTicketFn = httpsCallable<RemoveExpiredTicketRequest, RemoveExpiredTicketResponse>(functions, 'removeExpiredTicket');
const reorderQueueFn = httpsCallable<ReorderQueueRequest, ReorderQueueResponse>(functions, 'reorderQueue');
const clearClientHistoryFn = httpsCallable<ClearClientHistoryRequest, ClearClientHistoryResponse>(functions, 'clearClientHistory');
const archiveUserNotificationsFn = httpsCallable<{}, ArchiveNotificationsResponse>(functions, 'archiveUserNotifications');
const requestOvertimeTicketFn = httpsCallable<RequestOvertimeTicketRequest, RequestOvertimeTicketResponse>(functions, 'requestOvertimeTicket');
const respondOvertimeRequestFn = httpsCallable<RespondOvertimeRequest, RespondOvertimeResponse>(functions, 'respondOvertimeRequest');
const getClientPhoneFn = httpsCallable<{ queueId: string }, { phone: string | null }>(functions, 'getClientPhone');

/**
 * Get the phone number of the client on a queue ticket.
 * Authorized for: assigned barber, franchise admins/owners, super_admin.
 */
export async function getClientPhone(queueId: string): Promise<string | null> {
  const result = await getClientPhoneFn({ queueId });
  return result.data?.phone ?? null;
}

/**
 * Take a queue ticket
 */
export async function takeTicket(data: TakeTicketRequest): Promise<TakeTicketResponse> {
  try {
    const result: HttpsCallableResult<TakeTicketResponse> = await takeTicketFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error taking ticket:', error);
    throw new Error(error.message || 'Failed to take ticket');
  }
}

/**
 * Take a queue ticket with companions (for children, elderly, etc.)
 * Creates linked tickets for the main user and each companion
 */
export async function takeTicketWithCompanions(data: TakeTicketWithCompanionsRequest): Promise<TakeTicketWithCompanionsResponse> {
  try {
    console.log('🎟️ Taking ticket with companions:', data);
    const result: HttpsCallableResult<TakeTicketWithCompanionsResponse> = await takeTicketWithCompanionsFn(data);
    console.log('✅ Tickets created:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('Error taking ticket with companions:', error);
    throw new Error(error.message || 'Failed to take ticket with companions');
  }
}

/**
 * Advance queue - Call next client (barber action)
 */
export async function advanceQueue(data: AdvanceQueueRequest): Promise<AdvanceQueueResponse> {
  try {
    const result: HttpsCallableResult<AdvanceQueueResponse> = await advanceQueueFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error advancing queue:', error);
    throw new Error(error.message || 'Failed to advance queue');
  }
}

/**
 * Mark arrival at branch
 */
export async function markArrival(data: MarkArrivalRequest): Promise<MarkArrivalResponse> {
  try {
    const result: HttpsCallableResult<MarkArrivalResponse> = await markArrivalFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error marking arrival:', error);
    throw new Error(error.message || 'Failed to mark arrival');
  }
}

/**
 * Start service - Barber begins serving a client (arrived → in_service)
 */
export async function startService(data: StartServiceRequest): Promise<StartServiceResponse> {
  try {
    const result: HttpsCallableResult<StartServiceResponse> = await startServiceFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error starting service:', error);
    throw new Error(error.message || 'Failed to start service');
  }
}

/**
 * Complete ticket - Mark service as complete
 */
export async function completeTicket(data: CompleteTicketRequest): Promise<CompleteTicketResponse> {
  try {
    const result: HttpsCallableResult<CompleteTicketResponse> = await completeTicketFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error completing ticket:', error);
    throw new Error(error.message || 'Failed to complete ticket');
  }
}

/**
 * Cancel ticket - Cancel a queue ticket
 */
export async function cancelTicket(data: CancelTicketRequest): Promise<CancelTicketResponse> {
  try {
    const result: HttpsCallableResult<CancelTicketResponse> = await cancelTicketFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error cancelling ticket:', error);
    throw new Error(error.message || 'Failed to cancel ticket');
  }
}

/**
 * Remove expired/cancelled ticket
 * Archives and removes terminal state tickets from the queue view
 */
export async function removeExpiredTicket(data: RemoveExpiredTicketRequest): Promise<RemoveExpiredTicketResponse> {
  try {
    const result: HttpsCallableResult<RemoveExpiredTicketResponse> = await removeExpiredTicketFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error removing expired ticket:', error);
    throw new Error(error.message || 'Failed to remove ticket');
  }
}

/** Estados que cuentan como "turno activo" de un usuario. */
const ESTADOS_ACTIVOS = ['waiting', 'notified', 'arrived', 'in_service', 'finishing'];

/**
 * Ordena los turnos de un mismo usuario: primero el principal (el suyo), después
 * los acompañantes por posición.
 *
 * Hace falta porque la consulta no lleva `orderBy`, y en ese caso Firestore
 * devuelve los documentos ordenados por ID. Quedarse con `docs[0]` significaba
 * elegir el turno "según qué ID cayera antes alfabéticamente": con un
 * acompañante, la pantalla del cliente podía mostrarle el turno de su
 * acompañante (otro barbero, otra posición) como si fuera el propio.
 */
function ordenarTurnosDelUsuario(tickets: QueueTicket[]): QueueTicket[] {
  return [...tickets].sort((a, b) => {
    const aEsAcompanante = a.isCompanion === true;
    const bEsAcompanante = b.isCompanion === true;
    if (aEsAcompanante !== bEsAcompanante) return aEsAcompanante ? 1 : -1;
    return (a.position ?? 0) - (b.position ?? 0);
  });
}

/**
 * Subscribe to ALL of the user's active tickets at a branch (real-time).
 *
 * Un cliente puede tener varios turnos a la vez: el suyo y uno por cada
 * acompañante. Los del acompañante llevan el MISMO `userId` (para que las
 * notificaciones lleguen al que reservó) más `isCompanion`, `companionName` y
 * `parentQueueId` — ver `takeTicketWithCompanions` en functions.
 *
 * El primer elemento es siempre el turno propio; el resto, los acompañantes.
 */
export function subscribeToUserActiveTickets(
  userId: string,
  branchId: string,
  onUpdate: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, 'queues'),
    where('userId', '==', userId),
    where('branchId', '==', branchId),
    where('status', 'in', ESTADOS_ACTIVOS)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tickets = snapshot.docs.map((d) => ({
        ...d.data(),
        queueId: d.id,
      }) as QueueTicket);
      onUpdate(ordenarTurnosDelUsuario(tickets));
    },
    (error) => {
      console.error('Error subscribing to user active tickets:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to user's own active ticket at a branch (real-time).
 *
 * Devuelve SOLO el turno propio; los de los acompañantes se ignoran aquí. Para
 * mostrarlos todos hay que usar `subscribeToUserActiveTickets`.
 */
export function subscribeToUserActiveTicket(
  userId: string,
  branchId: string,
  onUpdate: (ticket: QueueTicket | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return subscribeToUserActiveTickets(
    userId,
    branchId,
    (tickets) => onUpdate(tickets.length > 0 ? tickets[0] : null),
    onError
  );
}

/**
 * Get user's active ticket at a branch (one-time read)
 * @deprecated Use subscribeToUserActiveTicket for real-time updates
 */
export async function getUserActiveTicket(userId: string, branchId: string): Promise<QueueTicket | null> {
  try {
    const q = query(
      collection(db, 'queues'),
      where('userId', '==', userId),
      where('branchId', '==', branchId),
      where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service', 'finishing'])
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    } else {
      // Mismo criterio que en la versión en tiempo real: el turno propio primero,
      // nunca el del acompañante por azar del ID de documento.
      const tickets = ordenarTurnosDelUsuario(
        snapshot.docs.map((d) => ({ ...d.data(), queueId: d.id }) as QueueTicket)
      );
      return tickets[0];
    }
  } catch (error) {
    console.error('Error getting user active ticket:', error);
    return null;
  }
}

/**
 * Get active queue for a branch (real-time)
 */
export function subscribeToQueueByBranch(
  branchId: string,
  onUpdate: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, 'queues'),
    where('branchId', '==', branchId),
    where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service', 'finishing']),
    orderBy('position', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tickets = snapshot.docs.map(doc => ({
        ...doc.data(),
        queueId: doc.id
      } as QueueTicket));
      onUpdate(tickets);
    },
    (error) => {
      console.error('Error subscribing to queue:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to queue by barber ID (real-time)
 * Used by barbers to see only their assigned tickets
 */
export function subscribeToQueueByBarber(
  barberId: string,
  branchId: string,
  onUpdate: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  console.log('🔥 subscribeToQueueByBarber called with barberId:', barberId, 'branchId:', branchId);

  const q = query(
    collection(db, 'queues'),
    where('branchId', '==', branchId),
    where('barberId', '==', barberId),
    where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service', 'finishing']),
    orderBy('position', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      console.log('📨 Barber queue snapshot received:', {
        barberId,
        branchId,
        docsCount: snapshot.docs.length,
        docs: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      });
      const tickets = snapshot.docs.map(doc => ({
        ...doc.data(),
        queueId: doc.id
      } as QueueTicket));
      onUpdate(tickets);
    },
    (error) => {
      console.error('❌ Error subscribing to barber queue:', error);
      onError?.(error);
    }
  );
}

/**
 * Get specific ticket by ID (real-time)
 */
export function subscribeToTicket(
  queueId: string,
  onUpdate: (ticket: QueueTicket | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const docRef = doc(db, 'queues', queueId);

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onUpdate({
          ...snapshot.data(),
          queueId: snapshot.id
        } as QueueTicket);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      console.error('Error subscribing to ticket:', error);
      onError?.(error);
    }
  );
}

/**
 * Get ticket details (one-time read)
 */
export async function getTicket(queueId: string): Promise<QueueTicket | null> {
  try {
    const docRef = doc(db, 'queues', queueId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        queueId: docSnap.id
      } as QueueTicket;
    }

    return null;
  } catch (error) {
    console.error('Error getting ticket:', error);
    return null;
  }
}

/**
 * Calculate time remaining on timer
 */
export function getTimeRemaining(timerExpiry: Timestamp | null): number {
  if (!timerExpiry) return 0;

  const expiryMs = timerExpiry.toMillis();
  const nowMs = Date.now();
  const remaining = expiryMs - nowMs;

  return Math.max(0, Math.floor(remaining / 1000)); // seconds
}

/**
 * Format time remaining as HH:MM:SS or MM:SS
 */
export function formatTimeRemaining(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Subscribe to pending tickets for a specific barber (real-time)
 */
export function subscribeToPendingTicketsByBarber(
  barberId: string,
  branchId: string | undefined,
  onUpdate: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  let q = query(
    collection(db, 'queues'),
    where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service', 'finishing']),
    orderBy('position', 'asc')
  );

  // Filter by branchId first (required for security rules)
  if (branchId) {
    q = query(q, where('branchId', '==', branchId));
  }

  // Then filter by barberId
  q = query(q, where('barberId', '==', barberId));

  return onSnapshot(
    q,
    (snapshot) => {
      const tickets = snapshot.docs.map(doc => ({
        ...doc.data(),
        queueId: doc.id
      } as QueueTicket));
      console.log(`📊 subscribeToPendingTicketsByBarber(${barberId}, ${branchId}) - Found ${tickets.length} tickets (real-time)`);
      onUpdate(tickets);
    },
    (error) => {
      console.error('Error subscribing to pending tickets:', error);
      onError?.(error);
    }
  );
}

/**
 * Get pending tickets for a specific barber (one-time read)
 * @deprecated Use subscribeToPendingTicketsByBarber for real-time updates
 */
export async function getPendingTicketsByBarber(barberId: string, branchId?: string): Promise<QueueTicket[]> {
  try {
    let q = query(
      collection(db, 'queues'),
      where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service', 'finishing']),
      orderBy('position', 'asc')
    );

    // Filter by branchId first (required for security rules)
    if (branchId) {
      q = query(q, where('branchId', '==', branchId));
    }

    // Then filter by barberId
    q = query(q, where('barberId', '==', barberId));

    const snapshot = await getDocs(q);
    const tickets = snapshot.docs.map(doc => ({
      ...doc.data(),
      queueId: doc.id
    } as QueueTicket));
    console.log(`📊 getPendingTicketsByBarber(${barberId}, ${branchId}) - Found ${tickets.length} tickets from server`);
    return tickets;
  } catch (error) {
    console.error('Error getting pending tickets:', error);
    return [];
  }
}

/**
 * Calculate estimated wait time based on queue
 * Returns estimated time in minutes and estimated completion time
 */
export interface QueueEstimation {
  estimatedWaitMinutes: number; // Time until you should arrive at the barbershop
  estimatedCompletionTime: Date; // Time when your service will be completed
  queuePosition: number;
  totalInQueue: number;
  /**
   * Minutos que quedan del descanso temporal del barbero (0 si no hay).
   *
   * Hace falta aparte porque `estimatedWaitMinutes` NO es una espera cuando no
   * hay nadie delante: ahí son los 20 minutos de cortesía que tiene el cliente
   * para llegar (ver `triggers.ts:101` y el `timerExpiry` que se calcula con ese
   * mismo número). Sin este dato no se puede distinguir "el barbero está libre,
   * puedes venir ya" de "el barbero está comiendo, ven a las 17:11".
   */
  remainingBreakMinutes?: number;
}

export async function calculateQueueEstimation(
  barberId: string,
  serviceDuration: number
): Promise<QueueEstimation> {
  try {
    console.log('📊 Calculating queue estimation for barber:', barberId);
    const pendingTickets = await getPendingTicketsByBarber(barberId);
    console.log('📊 Pending tickets found:', pendingTickets.length, pendingTickets);

    // Calculate wait time before being served (not including your service)
    let waitBeforeService = 0;
    let ticketsForBarber = 0;
    let skippedTickets = 0;

    for (const ticket of pendingTickets) {
      // 🔥 BUG FIX: Validate that ticket actually belongs to this barber
      // Some tickets may have invalid barberIds (userIds instead of barberIds)
      if (ticket.barberId !== barberId) {
        console.warn('⚠️ Skipping ticket with mismatched barberId:', {
          ticketId: ticket.queueId,
          ticketBarberId: ticket.barberId,
          expectedBarberId: barberId
        });
        skippedTickets++;
        continue;
      }

      // Use actual service duration, NOT estimatedWaitTime (which includes courtesy time for position 1)
      const ticketDuration = ticket.serviceDuration || 30;
      waitBeforeService += ticketDuration;
      ticketsForBarber++;
      console.log('📊 Adding ticket wait time:', ticketDuration, 'Total so far:', waitBeforeService);
    }

    if (skippedTickets > 0) {
      console.warn(`⚠️ Skipped ${skippedTickets} ticket(s) with invalid barberId`);
    }
    console.log(`✅ Counted ${ticketsForBarber} valid ticket(s) for barber ${barberId}`);

    // Si eres el primero (no hay nadie en cola), tienes 20 minutos para llegar
    const finalWaitMinutes = pendingTickets.length === 0 ? 20 : waitBeforeService;

    // Time when your service will be completed (wait + your service duration)
    const totalTimeUntilCompletion = finalWaitMinutes + serviceDuration;

    // Calculate estimated completion time
    const now = new Date();
    const estimatedCompletionTime = new Date(now.getTime() + totalTimeUntilCompletion * 60000);

    const result = {
      estimatedWaitMinutes: finalWaitMinutes, // 20 min si eres el primero, sino el calculado
      estimatedCompletionTime, // When your service will be complete
      queuePosition: pendingTickets.length + 1,
      totalInQueue: pendingTickets.length
    };

    console.log('📊 Queue estimation result:', result);
    return result;
  } catch (error) {
    console.error('❌ Error calculating queue estimation:', error);
    // Return default estimation - you're first in line
    const now = new Date();
    const defaultResult = {
      estimatedWaitMinutes: 20, // 20 minutos para llegar si eres el primero
      estimatedCompletionTime: new Date(now.getTime() + (20 + serviceDuration) * 60000),
      queuePosition: 1,
      totalInQueue: 0
    };
    console.log('📊 Returning default estimation:', defaultResult);
    return defaultResult;
  }
}

/**
 * Format estimated time as "HH:MM"
 */
export function formatEstimatedTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Reorder queue tickets - Update positions of multiple tickets
 */
export async function reorderQueue(data: ReorderQueueRequest): Promise<ReorderQueueResponse> {
  try {
    const result: HttpsCallableResult<ReorderQueueResponse> = await reorderQueueFn(data);
    return result.data;
  } catch (error: any) {
    console.error('Error reordering queue:', error);
    throw new Error(error.message || 'Failed to reorder queue');
  }
}

/**
 * Clear client history - Delete all completed/cancelled tickets for current user
 */
export async function clearClientHistory(): Promise<ClearClientHistoryResponse> {
  try {
    const result: HttpsCallableResult<ClearClientHistoryResponse> = await clearClientHistoryFn({});
    return result.data;
  } catch (error: any) {
    console.error('Error clearing client history:', error);
    throw new Error(error.message || 'Failed to clear client history');
  }
}

/**
 * Archive user notifications - Mark all user notifications as archived (hidden)
 */
export async function archiveUserNotifications(): Promise<ArchiveNotificationsResponse> {
  try {
    console.log('🔧 queueService.archiveUserNotifications - Calling Cloud Function...');
    const result: HttpsCallableResult<ArchiveNotificationsResponse> = await archiveUserNotificationsFn({});
    console.log('✅ queueService.archiveUserNotifications - Success:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('❌ queueService.archiveUserNotifications - Error:', {
      code: error.code,
      message: error.message,
      details: error.details,
      fullError: error
    });
    throw new Error(error.message || 'Failed to archive notifications');
  }
}

/**
 * Request an overtime ticket - When client wants to book outside barber's working hours
 * Creates a ticket with pending overtime request that needs barber approval
 */
export async function requestOvertimeTicket(data: RequestOvertimeTicketRequest): Promise<RequestOvertimeTicketResponse> {
  try {
    console.log('⏰ queueService.requestOvertimeTicket - Calling Cloud Function...', data);
    const result: HttpsCallableResult<RequestOvertimeTicketResponse> = await requestOvertimeTicketFn(data);
    console.log('✅ queueService.requestOvertimeTicket - Success:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('❌ queueService.requestOvertimeTicket - Error:', {
      code: error.code,
      message: error.message,
      details: error.details,
      fullError: error
    });
    throw new Error(error.message || 'Failed to request overtime ticket');
  }
}

/**
 * Respond to overtime request - Barber accepts or rejects the overtime request
 */
export async function respondOvertimeRequest(data: RespondOvertimeRequest): Promise<RespondOvertimeResponse> {
  try {
    console.log('⏰ queueService.respondOvertimeRequest - Calling Cloud Function...', data);
    const result: HttpsCallableResult<RespondOvertimeResponse> = await respondOvertimeRequestFn(data);
    console.log('✅ queueService.respondOvertimeRequest - Success:', result.data);
    return result.data;
  } catch (error: any) {
    console.error('❌ queueService.respondOvertimeRequest - Error:', {
      code: error.code,
      message: error.message,
      details: error.details,
      fullError: error
    });
    throw new Error(error.message || 'Failed to respond to overtime request');
  }
}

/**
 * Subscribe to pending overtime requests for a specific barber (real-time)
 * Used by barbers to see incoming overtime approval requests
 */
export function subscribeToPendingOvertimeRequests(
  barberId: string,
  branchId: string,
  onUpdate: (tickets: QueueTicket[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  console.log('⏰ subscribeToPendingOvertimeRequests - barberId:', barberId, 'branchId:', branchId);

  // Query tickets with pending overtime requests for this barber
  const q = query(
    collection(db, 'queues'),
    where('branchId', '==', branchId),
    where('overtimeRequest.barberId', '==', barberId),
    where('overtimeRequest.status', '==', 'pending')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const tickets = snapshot.docs.map(doc => ({
        ...doc.data(),
        queueId: doc.id
      } as QueueTicket));
      console.log(`⏰ Found ${tickets.length} pending overtime requests for barber ${barberId}`);
      onUpdate(tickets);
    },
    (error) => {
      console.error('❌ Error subscribing to overtime requests:', error);
      onError?.(error);
    }
  );
}
